/**
 * A FÉLKÉSZ SOROZAT MEGŐRZÉSE — az Sz6 huszonnégy órája.
 *
 * A fotózás közben az ügyfél telefonja bármit tehet: hívás jön, a Safari
 * lapot ürít memóriahiány miatt, valaki átvált egy másik appra és vissza.
 * A `useCaptureSession` állapota ilyenkor elvész — a papír viszont ott van a
 * kezében, és a munkát elölről kezdeni bosszantóbb, mint el sem kezdeni.
 *
 * ── AMIT ÍGÉRÜNK, ÉS AMIT NEM ───────────────────────────────────────────
 *
 * ⚠️ A MEGŐRZÉS SOHA NEM FELTÉTELE A MŰKÖDÉSNEK. Minden művelet itt elnyeli
 * a hibát, és úgy viselkedik, mintha nem lenne tároló: a betöltés `null`-t
 * ad, a mentés csendben nem csinál semmit. Privát ablakban, letiltott
 * sütiknél és iOS-en „Tárhely megtagadva" esetén az `indexedDB.open` MAGA
 * dobhat vagy örökre `blocked` maradhat — ha ez elszállhatna, a kamera sem
 * indulna el, és egy MELLÉKES kényelmi funkció miatt veszne el a fő út.
 *
 * ⚠️ A 24 ÓRA NEM GARANCIA, HANEM ŐSZINTESÉG. A `szkenneles-terv.md` §6
 * szerint a Safari ITP kb. hét nap használatlanság után üríthet, és a
 * tárhelyszűke bármikor. Ezért nem ígérünk hosszabbat, mint amit egy
 * böngésző reálisan megtart — a rövid, biztosan tartható ígéret jobb, mint
 * egy hosszú, amit néha megszeg.
 *
 * ── MIÉRT KÉT TÁR ───────────────────────────────────────────────────────
 *
 * A `pages` a bájtokat tartja, a `meta` a sorrendet és az időt. Egyetlen
 * rekordban tartva MINDEN változás — egy törlés, egy átrendezés — újraírná
 * az EGÉSZ sorozatot: húsz lapnál harminc megabájt mozogna azért, mert két
 * oldal helyet cserélt. Így a bájtok laponként EGYSZER íródnak, és a
 * sorrend cseréje egy pár száz bájtos rekord.
 *
 * ── MIÉRT `ArrayBuffer`, ÉS NEM `Blob` ──────────────────────────────────
 *
 * ⚠️ A `Blob` ELVILEG tárolható IndexedDB-ben, GYAKORLATBAN a WebKit évekig
 * nem tudta: iOS-en a visszaolvasott `Blob` üresen vagy hibával jött, és ez
 * a felület elsődlegesen iOS-telefonokon fut. A nyers bájtok minden
 * implementációban ugyanazt jelentik, és a `type` mellé írva a `Blob`
 * visszaépítése egy sor. Az ár egy másolat a memóriában mentéskor — ezt a
 * telefon elbírja, egy üresen visszajövő oldalt nem.
 */

import type { Me } from '@/lib/api-client';
import type { QuadCorners } from './document-scanner';

const DB_NAME = 'fakt-capture';
const DB_VERSION = 1;
const META_STORE = 'meta';
const PAGE_STORE = 'pages';

/** Az Sz6 ígérete. Lejárat után a sorozat csendben törlődik. */
export const SESSION_TTL_MS = 24 * 60 * 60 * 1000;

export interface StoredPage {
  id: string;
  blob: Blob;
  cropped: boolean;
  /** Az eredeti fotó és a vágás sarkai (#89) — régebbi rekordon hiányoznak. */
  original?: Blob;
  corners?: QuadCorners | null;
}

export interface StoredSession {
  pages: StoredPage[];
  /** Mikor került bele az ELSŐ lap — ezt mondjuk meg a felhasználónak. */
  startedAt: number;
}

interface MetaRecord {
  key: string;
  order: string[];
  startedAt: number;
  updatedAt: number;
}

interface PageRecord {
  /** `${sessionKey}::${pageId}` — a lapok globálisan egyedi kulcsa. */
  id: string;
  sessionKey: string;
  pageId: string;
  /** A NYERS BÁJTOK, nem `Blob` — lásd a fejlécet. */
  bytes: ArrayBuffer;
  /** A `Blob` visszaépítéséhez; enélkül `application/octet-stream` lenne. */
  type: string;
  cropped: boolean;
  /**
   * AZ EREDETI FOTÓ BÁJTJAI (#89). Opcionális, és ezért nincs sémaverzió-
   * emelés: a #89 előtti rekordokon egyszerűen hiányzik, és az a lap a
   * visszatöltés után vágás nélkül marad — nem dobjuk el.
   */
  originalBytes?: ArrayBuffer;
  originalType?: string;
  corners?: QuadCorners | null;
}

/**
 * EGY `Blob` NYERS BÁJTJAI.
 *
 * ⚠️ A `Blob.arrayBuffer()` CSAK iOS SAFARI 14 ÓTA LÉTEZIK (2020 ősz). Ez a
 * felület régebbi telefonokon is fut, és ott az `arrayBuffer is not a
 * function` nem egy elmaradt megőrzés lenne, hanem egy LEKEZELETLEN
 * elutasítás a fotózás közepén. A `FileReader` ugyanazt adja, és minden
 * böngészőben megvan — a régebbi út tehát nem tartalék, hanem a padló.
 */
function toBytes(blob: Blob): Promise<ArrayBuffer> {
  if (typeof blob.arrayBuffer === 'function') {
    return blob.arrayBuffer();
  }

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as ArrayBuffer);
    reader.onerror = () => reject(reader.error);
    reader.readAsArrayBuffer(blob);
  });
}

function openDatabase(): Promise<IDBDatabase | null> {
  return new Promise((resolve) => {
    let request: IDBOpenDBRequest;
    try {
      // ⚠️ A HÍVÁS MAGA IS DOBHAT — iOS-en a letiltott tárhely nem `onerror`,
      // hanem `SecurityError` a hívás helyén. Egy `try` nélküli `open` itt
      // magával vinné a kamerát is.
      request = indexedDB.open(DB_NAME, DB_VERSION);
    } catch {
      resolve(null);
      return;
    }

    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(META_STORE)) {
        db.createObjectStore(META_STORE, { keyPath: 'key' });
      }
      if (!db.objectStoreNames.contains(PAGE_STORE)) {
        const store = db.createObjectStore(PAGE_STORE, { keyPath: 'id' });
        store.createIndex('sessionKey', 'sessionKey', { unique: false });
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => resolve(null);
    // A `blocked` eset (egy másik lap régebbi sémával tartja nyitva) NEM
    // kap időzítőt: a promise egyszerűen nem dől el, amíg a másik lap be nem
    // zár. Ezt a hívók úgy kezelik, hogy a megőrzés „még tölt" — nem
    // akadályozza a fotózást.
    request.onblocked = () => resolve(null);
  });
}

/** Egy `IDBRequest` promise-ban. `null`, ha elszállt — dobni nem dobunk. */
function ask<T>(request: IDBRequest<T>): Promise<T | null> {
  return new Promise((resolve) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => resolve(null);
  });
}

function finished(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => resolve();
    tx.onabort = () => resolve();
  });
}

/**
 * A SOROZAT KIÍRÁSA.
 *
 * A `pages` a jelenlegi teljes sorozat, a megjelenítés sorrendjében. Ami a
 * tárolóban van, de a listában nincs, az TÖRLŐDIK — így egy `remove` után
 * nem maradnak árva bájtok, amiket aztán senki nem takarítana el.
 */
export async function saveSession(
  sessionKey: string,
  pages: readonly StoredPage[],
  now = Date.now()
): Promise<void> {
  // A KIOLVASÁS MINDIG A NYITÁS ELŐTT — lásd a `put`-nál álló figyelmeztetést.
  const prepared = await Promise.all(
    pages.map(async (page) => ({
      id: page.id,
      bytes: await toBytes(page.blob),
      type: page.blob.type,
      cropped: page.cropped,
      originalBytes: page.original ? await toBytes(page.original) : undefined,
      originalType: page.original?.type,
      corners: page.corners ?? null,
    }))
  );

  const db = await openDatabase();
  if (!db) {
    return;
  }

  try {
    const tx = db.transaction([META_STORE, PAGE_STORE], 'readwrite');
    const meta = tx.objectStore(META_STORE);
    const pageStore = tx.objectStore(PAGE_STORE);

    if (pages.length === 0) {
      // ÜRES SOROZAT = NINCS SOROZAT. Egy nulla lapos rekord csak arra lenne
      // jó, hogy a következő nyitáskor rákérdezzünk a semmire.
      meta.delete(sessionKey);
    } else {
      const existing = (await ask(meta.get(sessionKey))) as MetaRecord | null;
      meta.put({
        key: sessionKey,
        order: prepared.map((page) => page.id),
        // A KEZDÉS IDEJE NEM MOZDUL a további lapokkal: a felhasználónak azt
        // mondjuk meg, mikor KEZDTE, nem azt, mikor nyúlt hozzá utoljára.
        startedAt: existing?.startedAt ?? now,
        updatedAt: now,
      } satisfies MetaRecord);
    }

    const keep = new Set(prepared.map((page) => `${sessionKey}::${page.id}`));
    const stored = (await ask(pageStore.index('sessionKey').getAllKeys(sessionKey))) ?? [];
    for (const key of stored) {
      if (typeof key === 'string' && !keep.has(key)) {
        pageStore.delete(key);
      }
    }

    for (const page of prepared) {
      // ⚠️ A BÁJTOKAT A TRANZAKCIÓN KÍVÜL OLVASSUK KI. Egy `await` egy nem
      // IndexedDB-eseményre a tranzakciót LEZÁRJA (a böngésző akkor
      // commitol, amikor nincs több függő kérése) — a `blob.arrayBuffer()`
      // pedig pont ilyen. Ezért a `bytes` már készen érkezik ide.
      pageStore.put({
        id: `${sessionKey}::${page.id}`,
        sessionKey,
        pageId: page.id,
        bytes: page.bytes,
        type: page.type,
        cropped: page.cropped,
        originalBytes: page.originalBytes,
        originalType: page.originalType,
        corners: page.corners,
      } satisfies PageRecord);
    }

    await finished(tx);
  } catch {
    // A tranzakció bármikor elszállhat (tárhely megtelt, a lap elhagyja a
    // memóriát). A megőrzés ilyenkor kimarad — a fotózás megy tovább.
  } finally {
    db.close();
  }
}

/**
 * A MEGŐRZÖTT SOROZAT VISSZAOLVASÁSA.
 *
 * `null`, ha nincs, ha lejárt, vagy ha a tároló elérhetetlen. A LEJÁRT
 * SOROZATOT MINDJÁRT TÖRLI IS — a takarításnak nem kell külön alkalom, a
 * nyitás maga az alkalom.
 */
export async function loadSession(
  sessionKey: string,
  now = Date.now()
): Promise<StoredSession | null> {
  const db = await openDatabase();
  if (!db) {
    return null;
  }

  try {
    const tx = db.transaction([META_STORE, PAGE_STORE], 'readonly');
    const meta = (await ask(tx.objectStore(META_STORE).get(sessionKey))) as MetaRecord | null;
    if (!meta) {
      return null;
    }

    if (now - meta.updatedAt >= SESSION_TTL_MS) {
      db.close();
      await clearSession(sessionKey);
      return null;
    }

    const records = ((await ask(
      tx.objectStore(PAGE_STORE).index('sessionKey').getAll(sessionKey)
    )) ?? []) as PageRecord[];

    const byId = new Map(records.map((record) => [record.pageId, record]));
    const pages: StoredPage[] = [];
    for (const id of meta.order) {
      const record = byId.get(id);
      if (record) {
        pages.push({
          id,
          blob: new Blob([record.bytes], { type: record.type }),
          cropped: record.cropped,
          original: record.originalBytes
            ? new Blob([record.originalBytes], { type: record.originalType ?? 'image/jpeg' })
            : undefined,
          corners: record.corners ?? null,
        });
      }
      // ⚠️ A HIÁNYZÓ LAPOT ÁTUGORJUK, nem szakítjuk meg a visszatöltést. Egy
      // félbeszakadt írás után jobb négy lapot visszaadni ötből, mint
      // nullát — és a felhasználó az áttekintőn látja, mi van meg.
    }

    return pages.length > 0 ? { pages, startedAt: meta.startedAt } : null;
  } catch {
    return null;
  } finally {
    db.close();
  }
}

/**
 * MINDEN SOROZAT TÖRLÉSE (#76) — kilépéskor, és a maszk elején és végén.
 *
 * Egy eszközön két identitás ülhet egymás után: az ügyfél és a nevében
 * belépett könyvelő. A félbehagyott fotók bájtjai ne maradjanak a másiknak.
 * A kulcs ezen felül is identitáshoz kötött (`captureKey`) — a törlés az,
 * ami a lemezről is elviszi őket.
 */
export async function clearAllSessions(): Promise<void> {
  const db = await openDatabase();
  if (!db) {
    return;
  }

  try {
    const tx = db.transaction([META_STORE, PAGE_STORE], 'readwrite');
    tx.objectStore(META_STORE).clear();
    tx.objectStore(PAGE_STORE).clear();
    await finished(tx);
  } catch {
    // a takarítás elmaradása nem hiba a felhasználónak — a lejárat elviszi
  } finally {
    db.close();
  }
}

/**
 * A SOROZAT KULCSA — az identitáshoz kötve (#76).
 *
 * Egy `request:<id>` kulcs csak a kérést nevezné meg, a böngészőt használó
 * embert nem: ugyanazon a gépen a maszkolt könyvelő folytathatná az ügyfél
 * félbehagyott fotózását, és fordítva. A maszknál a munkamenet a principal,
 * mert két maszk ugyanannál a könyvelőnél is két külön alkalom.
 */
export function captureKey(principal: string, requestId: string): string {
  return `${principal}:request:${requestId}`;
}

/**
 * A KÉRETLEN FELTÖLTÉS SOROZATÁNAK KULCSA (#85) — a lábsáv „Feltöltök
 * valamit"-jából indított fotózás. EGY kulcs, nem típusonként: a „folytatod?"
 * a félbehagyott papírra kérdez, nem arra, mit mondott róla az ügyfél — ha
 * közben mást választott, a sorozat attól még ugyanaz a köteg lap.
 */
export function freeCaptureKey(principal: string): string {
  return `${principal}:free`;
}

/** Ki fotóz — a maszknál a munkamenet, egyébként a felhasználó. */
export function capturePrincipal(actor: Me['actor']): string {
  return actor.kind === 'impersonation' ? `grant:${actor.grantId}` : `user:${actor.userId ?? ''}`;
}

/** A sorozat végleges törlése — sikeres küldés és explicit elvetés után. */
export async function clearSession(sessionKey: string): Promise<void> {
  const db = await openDatabase();
  if (!db) {
    return;
  }

  try {
    const tx = db.transaction([META_STORE, PAGE_STORE], 'readwrite');
    tx.objectStore(META_STORE).delete(sessionKey);

    const pageStore = tx.objectStore(PAGE_STORE);
    const keys = (await ask(pageStore.index('sessionKey').getAllKeys(sessionKey))) ?? [];
    for (const key of keys) {
      pageStore.delete(key);
    }

    await finished(tx);
  } catch {
    // ugyanaz, mint a mentésnél: a takarítás elmaradása nem hiba az
    // ügyfélnek, a lejárat úgyis elviszi
  } finally {
    db.close();
  }
}

/**
 * MINDEN LEJÁRT SOROZAT TAKARÍTÁSA — nem csak azé, amit épp nyitunk.
 *
 * ⚠️ ENÉLKÜL EGY SOHA ÚJRA MEG NEM NYITOTT KÉRÉS BÁJTJAI ÖRÖKRE OTT
 * MARADNÁNAK. A `loadSession` csak azt a sorozatot takarítja, amit
 * megkérdeznek tőle; egy visszavont vagy elfelejtett kérés sorozatát soha
 * senki nem kérdezi meg. Ezt a kamera indulásakor futtatjuk, egyszer.
 */
export async function sweepExpired(now = Date.now()): Promise<void> {
  const db = await openDatabase();
  if (!db) {
    return;
  }

  let expired: string[] = [];
  try {
    const tx = db.transaction(META_STORE, 'readonly');
    const all = ((await ask(tx.objectStore(META_STORE).getAll())) ?? []) as MetaRecord[];
    expired = all
      .filter((record) => now - record.updatedAt >= SESSION_TTL_MS)
      .map((record) => record.key);
  } catch {
    return;
  } finally {
    db.close();
  }

  for (const key of expired) {
    await clearSession(key);
  }
}
